import { isPublicPage } from '@feral/shared'
import { Navigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

const TRIED_KEY = 'fm-server-page-tried'

/** Back here within moments of trying means the server has no such page either: no second try. */
const TRIED_MS = 30_000
/** Asked once a page load (development renders twice, and the second would read the first's mark). */
const asked = new Map<string, boolean>()
function triedAlready(path: string): boolean {
  const known = asked.get(path)
  if (known !== undefined) return known
  asked.set(path, tried(path))
  return asked.get(path) as boolean
}
function tried(path: string): boolean {
  try {
    const [at, was] = (sessionStorage.getItem(TRIED_KEY) ?? '').split(' ')
    if (was === path && Date.now() - Number(at) < TRIED_MS) return true
    sessionStorage.setItem(TRIED_KEY, `${Date.now()} ${path}`)
  } catch {}
  return false
}

/**
 * Where an address the app has no screen for lands: the map. But a public page the server makes
 * (/partners, /how-it-works, …) only gets here when a service worker from before that page existed
 * answered with the app instead (bugs.md: /partners opened the front door). The newer worker is
 * usually downloaded and waiting, so this lets it take over and loads the page again, from the server.
 */
export function NotFound() {
  const path = window.location.pathname
  const [give, setGive] = useState(() => !isPublicPage(path) || triedAlready(path))
  useEffect(() => {
    if (give) return
    let off = false
    const reload = () => window.location.reload()
    ;(async () => {
      const reg = 'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration() : undefined
      if (!reg) return reload()
      await reg.update().catch(() => {})
      const next = reg.waiting ?? reg.installing
      if (!next) return reg.unregister().then(reload, reload)
      navigator.serviceWorker.addEventListener('controllerchange', reload, { once: true })
      if (next.state === 'installed') next.postMessage({ type: 'SKIP_WAITING' })
      else
        next.addEventListener('statechange', () => {
          if (next.state === 'installed') next.postMessage({ type: 'SKIP_WAITING' })
        })
      // If it never takes over, the map as before.
      setTimeout(() => !off && setGive(true), 10_000)
    })().catch(() => !off && setGive(true))
    return () => {
      off = true
    }
  }, [give])
  return give ? <Navigate to="/map" /> : null
}
